import { useState, useEffect, useRef, Fragment } from 'react'
import api from '../api/client'
import { corpusApi, Corpus } from '../api/corpora'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

// Compact number formatting for token counts (e.g. 1.2M, 34.5k).
function formatCompact(n?: number | null): string {
  if (n == null) return '0'
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(1)}B`
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1)}k`
  return String(n)
}

function fmtCost(c?: number | null): string {
  if (c == null) return '—'
  return c < 1 ? `$${c.toFixed(4)}` : `$${c.toFixed(2)}`
}

// Tooltip for the daily-activity chart: shows the three counts plus the day's
// estimated cost (which isn't a bar series).
function DailyTooltip({ active, payload, label }: any) {
  if (!active || !payload || !payload.length) return null
  const d = payload[0].payload || {}
  return (
    <div className="bg-white border rounded-lg shadow-sm px-3 py-2 text-xs">
      <div className="font-medium text-gray-800 mb-1">{label}</div>
      <div className="text-blue-600">Searches: {d.searches ?? 0}</div>
      <div className="text-purple-600">AI analyses: {d.ai_analyses ?? d.ai_batch ?? 0}</div>
      <div className="text-emerald-600">LLM requests: {d.llm_requests ?? 0}</div>
      <div className="text-gray-700 mt-1 pt-1 border-t">AI units: <span className="font-medium">{(d.ai_units ?? 0).toLocaleString()}</span></div>
      <div className="text-gray-700">Est. cost: <span className="font-medium text-green-700">{fmtCost(d.estimated_cost)}</span></div>
    </div>
  )
}

interface IngestionJob {
  job_id: string
  corpus_id?: string | null
  status: 'pending' | 'running' | 'complete' | 'error' | 'cancelled'
  params?: { query: string; year_from: number; year_to: number; oa_only: boolean; include_non_oa?: boolean }
  total_found: number
  total_new: number
  processed: number
  errors: number
  error_message?: string | null
  reverted_at?: string | null
  started_at?: string | null
  finished_at?: string | null
}

const INGEST_STATUS_STYLES: Record<IngestionJob['status'], string> = {
  pending: 'bg-gray-100 text-gray-700',
  running: 'bg-blue-100 text-blue-700',
  complete: 'bg-green-100 text-green-700',
  error: 'bg-red-100 text-red-700',
  cancelled: 'bg-orange-100 text-orange-700',
}

function timeAgo(iso?: string | null): string {
  if (!iso) return '—'
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000))
  if (sec < 60) return `${sec}s ago`
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`
  return `${Math.floor(sec / 86400)}d ago`
}

export default function AdminPage() {
  const [metrics, setMetrics] = useState<any>(null)
  const [users, setUsers] = useState<any[]>([])
  const [plans, setPlans] = useState<any[]>([])
  const [recentSearches, setRecentSearches] = useState<any[]>([])

  // --- Users tab: search + per-user drill-down + sorting ---
  const [userSearch, setUserSearch] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [userDetail, setUserDetail] = useState<any>(null)
  const [userDetailLoading, setUserDetailLoading] = useState(false)
  const [userSort, setUserSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'last_active', dir: 'desc' })
  const [resetResult, setResetResult] = useState<{ email: string; password: string } | null>(null)

  const toggleUserSort = (key: string) =>
    setUserSort(s => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }))

  const userSortValue = (u: any, key: string): number | string => {
    switch (key) {
      case 'email': return (u.email || '').toLowerCase()
      case 'plan': return u.plan || 'basic'
      case 'searches': return u.search_count || 0
      case 'ai_batch': return u.ai_batch_count || 0
      case 'llm_req': return u.llm_requests || 0
      case 'cache_hit': return u.cache_hit_tokens || 0
      case 'cache_miss': return u.cache_miss_tokens || 0
      case 'input': return (u.cache_hit_tokens || 0) + (u.cache_miss_tokens || 0)
      case 'output': return u.output_tokens || 0
      case 'cost': return u.estimated_cost || 0
      case 'last_active': return u.last_active ? new Date(u.last_active).getTime() : 0
      default: return 0
    }
  }

  // Sortable column header for the Users table.
  const sortHeader = (k: string, label: string, align: 'left' | 'right' = 'left') => (
    <th
      onClick={() => toggleUserSort(k)}
      className={`px-3 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-gray-900 ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      {label}
      <span className="ml-1 text-gray-400">{userSort.key === k ? (userSort.dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
    </th>
  )

  // Helper: FastAPI 422 errors return detail as an array of validation errors.
  // This normalises detail to a plain string so React never tries to render an object.
  const fmtErr = (e: any, fallback: string): string => {
    const detail = e?.response?.data?.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) {
      return detail.map((d: any) => d.msg || String(d)).join('; ') || fallback
    }
    return fallback
  }
  const [config, setConfig] = useState<any>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'plans' | 'config' | 'databases' | 'ingestion' | 'backup'>('overview')
  // Databases (corpora)
  const [corpora, setCorpora] = useState<Corpus[]>([])
  const [newDbName, setNewDbName] = useState('')
  const [newDbDesc, setNewDbDesc] = useState('')
  const [editDbId, setEditDbId] = useState('')      // corpus_id being renamed/edited inline
  const [editDbName, setEditDbName] = useState('')
  const [editDbDesc, setEditDbDesc] = useState('')
  const [ingCorpusId, setIngCorpusId] = useState('')
  const [range, setRange] = useState('30d')
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  // --- Backup tab state ---
  const [backupInfo, setBackupInfo] = useState<any>(null)
  const [backupBusy, setBackupBusy] = useState('')        // which action is in flight
  const [manuscriptRestoreState, setManuscriptRestoreState] = useState<string>('')
  const [exportCorpusId, setExportCorpusId] = useState('')  // '' = everything
  const userDbFileRef = useRef<HTMLInputElement>(null)
  const manuscriptFileRef = useRef<HTMLInputElement>(null)

  const formatBytes = (n: number): string => {
    if (!n) return '0 B'
    const u = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(n) / Math.log(1024))
    return `${(n / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${u[i]}`
  }

  const downloadBlob = async (path: string, busyKey: string) => {
    setError(''); setMessage(''); setBackupBusy(busyKey)
    try {
      const res = await api.get(path, { responseType: 'blob' })
      const cd = res.headers['content-disposition'] || ''
      const match = /filename="?([^"]+)"?/.exec(cd)
      const filename = match ? match[1] : 'backup'
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
      setMessage(`Downloaded ${filename}`)
    } catch (e: any) {
      setError(fmtErr(e, 'Export failed'))
    } finally {
      setBackupBusy('')
    }
  }

  const restoreUserDb = async (file: File) => {
    if (!confirm(`Restore user database from "${file.name}"?\n\nExisting rows are merged (matched by ID and updated); nothing is deleted.`)) return
    setError(''); setMessage(''); setBackupBusy('user-db-restore')
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await api.post('/admin/backup/user-db/restore', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      const tables = res.data?.tables || {}
      const total = Object.values(tables).reduce((s: number, n: any) => s + Number(n), 0)
      setMessage(`User database restored — ${total} rows across ${Object.keys(tables).length} tables.`)
      loadData()
    } catch (e: any) {
      setError(fmtErr(e, 'User database restore failed'))
    } finally {
      setBackupBusy('')
      if (userDbFileRef.current) userDbFileRef.current.value = ''
    }
  }

  const pollManuscriptRestore = async (taskId: string) => {
    try {
      const res = await api.get(`/admin/backup/manuscripts/restore/${taskId}`)
      const state = res.data?.state
      const stage = res.data?.info?.stage
      setManuscriptRestoreState(stage ? `${state} — ${stage}` : state)
      if (state === 'SUCCESS') {
        setBackupBusy(''); setMessage('Manuscripts database restored.'); loadData()
        return
      }
      if (state === 'FAILURE') {
        setBackupBusy(''); setError(`Manuscripts restore failed: ${res.data?.info?.error || 'unknown error'}`)
        return
      }
      setTimeout(() => pollManuscriptRestore(taskId), 2000)
    } catch (e: any) {
      setBackupBusy(''); setError(fmtErr(e, 'Lost track of restore task'))
    }
  }

  const restoreManuscripts = async (file: File) => {
    if (!confirm(`Restore databases from "${file.name}"?\n\nEach database (corpus) in the archive is installed into its own slot — databases not in the archive are left untouched. Any replaced files are kept under ".restore_prev" on the server. This runs in the background and can take a while.`)) return
    setError(''); setMessage(''); setBackupBusy('manuscripts-restore'); setManuscriptRestoreState('uploading')
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await api.post('/admin/backup/manuscripts/restore', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      if (res.data?.task_id) {
        setManuscriptRestoreState('queued')
        pollManuscriptRestore(res.data.task_id)
      } else {
        setBackupBusy('')
      }
    } catch (e: any) {
      setBackupBusy(''); setManuscriptRestoreState('')
      setError(fmtErr(e, 'Manuscripts restore failed'))
    } finally {
      if (manuscriptFileRef.current) manuscriptFileRef.current.value = ''
    }
  }

  // --- Ingestion tab state ---
  const currentYear = new Date().getFullYear()
  const [ingQuery, setIngQuery] = useState('extracellular vesicles')
  const [ingYearFrom, setIngYearFrom] = useState(2004)
  const [ingYearTo, setIngYearTo] = useState(currentYear)
  const [ingIncludeNonOa, setIngIncludeNonOa] = useState(false)
  // OA-only is simply the inverse of "include non-OA" — they were two checkboxes
  // for one dimension, and the backend always derives oa_only = !include_non_oa.
  const ingOaOnly = !ingIncludeNonOa
  const [ingQueryMode, setIngQueryMode] = useState<'structured' | 'raw'>('structured')
  const [ingPreview, setIngPreview] = useState<{ total_found: number; total_new: number; total_oa?: number; total_non_oa?: number } | null>(null)
  const [ingPreviewLoading, setIngPreviewLoading] = useState(false)
  const [ingStartLoading, setIngStartLoading] = useState(false)
  const [ingJobs, setIngJobs] = useState<IngestionJob[]>([])
  const ingPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const loadIngestionJobs = async () => {
    try {
      const res = await api.get<IngestionJob[]>('/admin/ingestion/jobs')
      setIngJobs(res.data)
    } catch {
      // silent
    }
  }

  const previewIngestion = async () => {
    setIngPreview(null)
    setIngPreviewLoading(true)
    setError('')
    try {
      const res = await api.post('/admin/ingestion/preview', {
        corpus_id: ingCorpusId || null,
        query: ingQuery,
        year_from: ingYearFrom,
        year_to: ingYearTo,
        oa_only: ingOaOnly,
        include_non_oa: ingIncludeNonOa,
        query_mode: ingQueryMode,
      })
      setIngPreview(res.data)
    } catch (e: any) {
      setError(fmtErr(e, 'Preview failed'))
    } finally {
      setIngPreviewLoading(false)
    }
  }

  const startIngestion = async () => {
    if (!ingCorpusId) { setError('Select a target database for ingestion.'); return }
    setIngStartLoading(true)
    setError('')
    setMessage('')
    try {
      const res = await api.post('/admin/ingestion/jobs', {
        corpus_id: ingCorpusId,
        query: ingQuery,
        year_from: ingYearFrom,
        year_to: ingYearTo,
        oa_only: ingOaOnly,
        include_non_oa: ingIncludeNonOa,
        query_mode: ingQueryMode,
      })
      setMessage(`Ingestion job ${res.data.job_id.slice(0, 8)}… started`)
      setIngPreview(null)
      await loadIngestionJobs()
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to start ingestion'))
    } finally {
      setIngStartLoading(false)
    }
  }

  const loadCorpora = async () => {
    try {
      const res = await corpusApi.adminList()
      setCorpora(res.data)
      setIngCorpusId(prev => prev || (res.data[0]?.corpus_id ?? ''))
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to load databases'))
    }
  }

  const createDatabase = async () => {
    const name = newDbName.trim()
    if (!name) return
    setError(''); setMessage('')
    try {
      await corpusApi.create({ name, description: newDbDesc.trim() || undefined })
      setNewDbName(''); setNewDbDesc('')
      setMessage(`Database "${name}" created`)
      await loadCorpora()
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to create database'))
    }
  }

  const deleteDatabase = async (c: Corpus) => {
    if (!confirm(`Delete database "${c.name}" and ALL its ingested manuscripts (${c.article_count.toLocaleString()})?\n\nThis cannot be undone.`)) return
    setError(''); setMessage('')
    try {
      await corpusApi.delete(c.corpus_id)
      if (ingCorpusId === c.corpus_id) setIngCorpusId('')
      setMessage(`Database "${c.name}" deleted`)
      await loadCorpora()
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to delete database'))
    }
  }

  const toggleDefaultDb = async (c: Corpus, val: boolean) => {
    setError(''); setMessage('')
    try {
      await corpusApi.update(c.corpus_id, { is_default: val })
      setCorpora(prev => prev.map(x => x.corpus_id === c.corpus_id ? { ...x, is_default: val } : x))
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to update default'))
    }
  }

  const startEditDb = (c: Corpus) => {
    setError(''); setMessage('')
    setEditDbId(c.corpus_id)
    setEditDbName(c.name)
    setEditDbDesc(c.description || '')
  }

  const cancelEditDb = () => { setEditDbId(''); setEditDbName(''); setEditDbDesc('') }

  const saveEditDb = async (c: Corpus) => {
    const name = editDbName.trim()
    if (!name) { setError('Name cannot be empty'); return }
    setError(''); setMessage('')
    try {
      // Only send what changed; description is sent even when cleared (null clears it).
      await corpusApi.update(c.corpus_id, { name, description: editDbDesc.trim() })
      setCorpora(prev => prev.map(x =>
        x.corpus_id === c.corpus_id ? { ...x, name, description: editDbDesc.trim() } : x))
      setMessage(`Database "${name}" updated`)
      cancelEditDb()
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to update database'))
    }
  }

  const saveUserCorpora = async (userId: string, corpusIds: string[]) => {
    setError(''); setMessage('')
    try {
      await corpusApi.setUserCorpora(userId, corpusIds)
      setMessage('Database access updated')
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to update database access'))
    }
  }

  // Load a past job's parameters into the ingestion form above so the admin can
  // tweak and re-launch. Does not start anything — just populates the inputs.
  const reuseIngestionJob = (j: IngestionJob) => {
    if (!j.params) return
    setIngQuery(j.params.query || '')
    setIngYearFrom(j.params.year_from ?? 2004)
    setIngYearTo(j.params.year_to ?? currentYear)
    setIngIncludeNonOa(!!j.params.include_non_oa)
    setIngPreview(null)
    setError('')
    setMessage('Query loaded into the form above — edit and launch when ready.')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const cancelIngestion = async (jobId: string) => {
    if (!confirm('Cancel this ingestion job? Partial data already written stays.')) return
    try {
      await api.delete(`/admin/ingestion/jobs/${jobId}`)
      await loadIngestionJobs()
    } catch (e: any) {
      setError(fmtErr(e, 'Cancel failed'))
    }
  }

  const revertIngestion = async (j: IngestionJob) => {
    const dbName = corpora.find(c => c.corpus_id === j.corpus_id)?.name || 'this database'
    if (!confirm(
      `Delete the articles this job added to "${dbName}"?\n\n` +
      `Query: ${j.params?.query || '—'} (${j.params?.year_from}-${j.params?.year_to})\n` +
      `Up to ${j.total_new.toLocaleString()} articles will be removed from the corpus ` +
      `(their metadata, abstracts and full text). This cannot be undone — but you can re-ingest the same query later.`
    )) return
    setError(''); setMessage('')
    try {
      const res = await api.delete(`/admin/ingestion/jobs/${j.job_id}/articles`)
      const d = res.data || {}
      setMessage(
        `Removed ${Number(d.removed_metadata || 0).toLocaleString()} articles from "${d.database}" ` +
        `(${Number(d.removed_partition_files || 0).toLocaleString()} text files).` +
        (d.reconstructed ? ' Set reconstructed from this job’s text files (legacy job).' : '')
      )
      await loadIngestionJobs()
      loadCorpora()  // refresh article counts shown in the form's DB picker
    } catch (e: any) {
      setError(fmtErr(e, 'Delete failed'))
    }
  }

  // Poll the ingestion-jobs list while the tab is active and any job is in flight.
  useEffect(() => {
    if (activeTab !== 'ingestion') {
      if (ingPollRef.current) {
        clearInterval(ingPollRef.current)
        ingPollRef.current = null
      }
      return
    }
    loadIngestionJobs()
    ingPollRef.current = setInterval(() => {
      // Only poll while at least one job is in flight; otherwise back off.
      const hasActive = ingJobs.some(j => j.status === 'pending' || j.status === 'running')
      if (hasActive) loadIngestionJobs()
    }, 3000)
    return () => {
      if (ingPollRef.current) clearInterval(ingPollRef.current)
      ingPollRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, ingJobs.some(j => j.status === 'pending' || j.status === 'running')])

  useEffect(() => {
    loadData()
  }, [range, activeTab])

  // The ingestion form and the Databases tab both need the corpus list.
  useEffect(() => {
    if (['ingestion', 'databases', 'users', 'backup'].includes(activeTab)) loadCorpora()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  // Keep an open per-user drill-down in sync with the selected time range.
  useEffect(() => {
    if (activeTab === 'users' && selectedUserId) {
      api.get(`/admin/users/${selectedUserId}?range_param=${range}`)
        .then(res => setUserDetail(res.data))
        .catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range])

  const loadData = async () => {
    setError('')
    try {
      if (activeTab === 'overview') {
        const [ov, rs] = await Promise.all([
          api.get(`/admin/metrics/overview?range_param=${range}`),
          api.get('/admin/recent-searches?limit=20'),
        ])
        setMetrics(ov.data)
        setRecentSearches(rs.data)
      } else if (activeTab === 'users') {
        const res = await api.get(`/admin/metrics/users?range_param=${range}`)
        setUsers(res.data)
      } else if (activeTab === 'plans') {
        const res = await api.get('/admin/plans')
        setPlans(res.data)
      } else if (activeTab === 'config') {
        const res = await api.get('/admin/config')
        setConfig(res.data)
      } else if (activeTab === 'backup') {
        const res = await api.get('/admin/backup/info')
        setBackupInfo(res.data)
      }
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to load admin data'))
    }
  }

  const updatePlan = async (planName: string, daily: number, monthly: number) => {
    try {
      await api.put(`/admin/plans/${planName}`, { daily_limit: daily, monthly_limit: monthly })
      setMessage(`Plan ${planName} updated`)
      loadData()
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to update plan'))
    }
  }

  const updateUserPlan = async (userId: string, plan: string) => {
    setError(''); setMessage('')
    try {
      const res = await api.put(`/admin/users/${userId}/plan`, { plan })
      const d = res.data || {}
      // The new caps apply instantly — surface the now-effective limits so it's
      // clear the user is unblocked (used below the new daily/monthly ceilings).
      setMessage(
        d.daily_limit != null
          ? `Plan set to ${d.plan}. Limits apply immediately — daily ${d.used_daily}/${d.daily_limit}, monthly ${d.used_monthly}/${d.monthly_limit}.`
          : 'User plan updated'
      )
      loadData()
      if (selectedUserId === userId) openUserDetail(userId)
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to update user plan'))
    }
  }

  const openUserDetail = async (userId: string) => {
    if (selectedUserId === userId && userDetail) {
      // toggle closed
      setSelectedUserId(null)
      setUserDetail(null)
      return
    }
    setSelectedUserId(userId)
    setUserDetail(null)
    setUserDetailLoading(true)
    try {
      const res = await api.get(`/admin/users/${userId}?range_param=${range}`)
      setUserDetail(res.data)
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to load user details'))
      setSelectedUserId(null)
    } finally {
      setUserDetailLoading(false)
    }
  }

  const deleteUser = async (userId: string, email: string) => {
    if (!confirm(`Permanently delete ${email} and ALL their data (searches, datasets, analyses, usage)?\n\nThis cannot be undone.`)) return
    setError(''); setMessage('')
    try {
      await api.delete(`/admin/users/${userId}`)
      setMessage(`Deleted ${email}`)
      if (selectedUserId === userId) { setSelectedUserId(null); setUserDetail(null) }
      loadData()
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to delete user'))
    }
  }

  const resetPassword = async (userId: string, email: string) => {
    if (!confirm(`Generate a new temporary password for ${email}?\n\nTheir current password will stop working immediately.`)) return
    setError(''); setMessage(''); setResetResult(null)
    try {
      const res = await api.post(`/admin/users/${userId}/reset-password`)
      setResetResult({ email: res.data.email, password: res.data.temporary_password })
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to reset password'))
    }
  }

  // Robust copy: the async Clipboard API is unavailable over plain HTTP (only
  // secure contexts / localhost), so fall back to a hidden textarea + execCommand.
  const copyText = async (text: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
      } else {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.focus()
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
      setError(''); setMessage('Copied to clipboard')
    } catch {
      setError('Copy failed — select the text and copy manually')
    }
  }

  const updateConfig = async (field: string, value: any) => {
    try {
      await api.put('/admin/config', { [field]: value })
      setMessage('Config updated')
      loadData()
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to update config'))
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Admin Dashboard</h1>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
      {message && <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm">{message}</div>}

      <div className="flex gap-2 mb-6">
        {(['overview', 'users', 'plans', 'config', 'databases', 'ingestion', 'backup'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === tab ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
        <select
          value={range}
          onChange={e => setRange(e.target.value)}
          className="ml-auto px-3 py-2 border rounded-lg text-sm"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="all">All time</option>
        </select>
      </div>

      {activeTab === 'overview' && metrics && (
        <div className="space-y-6">
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="text-2xl font-bold text-blue-600">{metrics.totals.users}</div>
              <div className="text-sm text-gray-600">Users</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="text-2xl font-bold text-green-600">{metrics.totals.searches}</div>
              <div className="text-sm text-gray-600">Searches</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="text-2xl font-bold text-purple-600">{metrics.totals.ai_analyses}</div>
              <div className="text-sm text-gray-600">AI Analyses</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="text-2xl font-bold text-orange-600">{metrics.totals.llm_tokens?.toLocaleString()}</div>
              <div className="text-sm text-gray-600">LLM Tokens</div>
            </div>
          </div>

          {/* Token breakdown & cost estimate */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="font-semibold mb-4">Token Breakdown &amp; Cost Estimate</h3>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div>
                <div className="text-xs text-gray-500">Cache Hit</div>
                <div className="text-lg font-medium">{metrics.token_costs.cache_hit_tokens?.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Cache Miss</div>
                <div className="text-lg font-medium">{metrics.token_costs.cache_miss_tokens?.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Input total</div>
                <div className="text-lg font-medium">{((metrics.token_costs.cache_hit_tokens || 0) + (metrics.token_costs.cache_miss_tokens || 0)).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Output</div>
                <div className="text-lg font-medium">{metrics.token_costs.output_tokens?.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Total</div>
                <div className="text-lg font-medium">{metrics.token_costs.total_tokens?.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Est. Cost</div>
                <div className="text-lg font-semibold text-green-700">{fmtCost(metrics.token_costs.estimated_cost)}</div>
              </div>
            </div>
            {metrics.token_costs.estimated_cost == null && (
              <p className="text-xs text-gray-400 mt-3">Set token prices in the Config tab to see cost estimates.</p>
            )}
          </div>

          {/* Daily activity chart */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="font-semibold mb-1">Daily activity</h3>
            <p className="text-xs text-gray-500 mb-3">
              <span className="font-medium">AI analyses</span> = batch-analysis jobs users started.
              <span className="font-medium"> LLM requests</span> = individual model API calls (one job makes several;
              image generation and the regex builder also count). Hover a day for its estimated cost.
            </p>
            {(!metrics.daily || metrics.daily.length === 0) ? (
              <div className="text-sm text-gray-400">No activity in this range.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={metrics.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip content={<DailyTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="searches" stackId="a" fill="#3b82f6" name="Searches" />
                  <Bar dataKey="ai_analyses" stackId="a" fill="#a855f7" name="AI analyses" />
                  <Bar dataKey="llm_requests" stackId="b" fill="#10b981" name="LLM requests" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Recent searches */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <h3 className="font-semibold px-6 pt-5 pb-3">Recent searches</h3>
            {recentSearches.length === 0 ? (
              <div className="px-6 pb-5 text-sm text-gray-400">No searches yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-600">When</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-600">User</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-600">Query</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-600">Results</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {recentSearches.map(s => {
                      const kws = Array.isArray(s.query_params?.keywords)
                        ? s.query_params.keywords.map((k: any) => k.term || k).join(', ')
                        : ''
                      return (
                        <tr key={s.search_id} className="hover:bg-gray-50 align-top">
                          <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap" title={s.created_at}>{timeAgo(s.created_at)}</td>
                          <td className="px-4 py-2 text-xs text-gray-700">{s.user_email || '—'}</td>
                          <td className="px-4 py-2 text-xs text-gray-700"><div className="max-w-md break-words">{kws || '—'}</div></td>
                          <td className="px-4 py-2 text-xs text-gray-700 text-right">{s.result_count?.toLocaleString()}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'users' && (() => {
        const q = userSearch.trim().toLowerCase()
        const filteredUsers = q
          ? users.filter(u =>
              (u.email || '').toLowerCase().includes(q) ||
              (u.name || '').toLowerCase().includes(q))
          : users
        const sortedUsers = [...filteredUsers].sort((a, b) => {
          const va = userSortValue(a, userSort.key)
          const vb = userSortValue(b, userSort.key)
          const cmp = typeof va === 'string' && typeof vb === 'string'
            ? va.localeCompare(vb)
            : (va as number) - (vb as number)
          return userSort.dir === 'asc' ? cmp : -cmp
        })
        return (
        <div className="space-y-4">
          {resetResult && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-amber-900">
                    Temporary password for {resetResult.email}
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    Send this to the user's login email. It's shown only once — they should change it after signing in.
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="px-3 py-1.5 bg-white border rounded font-mono text-sm tracking-wide">{resetResult.password}</code>
                    <button
                      onClick={() => copyText(resetResult.password)}
                      className="text-xs px-2 py-1.5 bg-amber-600 text-white rounded hover:bg-amber-700"
                    >
                      Copy
                    </button>
                  </div>
                </div>
                <button onClick={() => setResetResult(null)} className="text-amber-500 hover:text-amber-800 text-lg leading-none">×</button>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              placeholder="Search users by email or name…"
              className="w-72 px-3 py-2 border rounded-lg text-sm"
            />
            <span className="text-xs text-gray-500">
              {filteredUsers.length} of {users.length} user{users.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {sortHeader('email', 'Email', 'left')}
                  {sortHeader('plan', 'Plan', 'left')}
                  {sortHeader('searches', 'Searches', 'right')}
                  {sortHeader('ai_batch', 'AI batch', 'right')}
                  {sortHeader('llm_req', 'LLM req', 'right')}
                  {sortHeader('cache_hit', 'Cache Hit', 'right')}
                  {sortHeader('cache_miss', 'Cache Miss', 'right')}
                  {sortHeader('input', 'Input', 'right')}
                  {sortHeader('output', 'Output', 'right')}
                  {sortHeader('cost', 'Est. Cost', 'right')}
                  {sortHeader('last_active', 'Last active', 'left')}
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Plan</th>
                  <th className="px-3 py-3 text-right font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredUsers.length === 0 && (
                  <tr><td colSpan={13} className="px-3 py-6 text-center text-sm text-gray-400">No users match “{userSearch}”.</td></tr>
                )}
                {sortedUsers.map(u => (
                  <Fragment key={u.user_id}>
                  <tr
                    onClick={() => openUserDetail(u.user_id)}
                    className={`hover:bg-gray-50 cursor-pointer ${selectedUserId === u.user_id ? 'bg-blue-50' : ''}`}
                  >
                    <td className="px-3 py-2 text-xs text-gray-700">
                      <span className="text-gray-400 mr-1">{selectedUserId === u.user_id ? '▾' : '▸'}</span>
                      <a
                        href={`mailto:${u.email}`}
                        onClick={e => e.stopPropagation()}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                        title={`Email ${u.email}`}
                      >
                        {u.email}
                      </a>
                    </td>
                    <td className="px-3 py-2">
                      <span className="bg-gray-100 px-2 py-0.5 rounded text-xs">{u.plan || 'basic'}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-right">{u.search_count}</td>
                    <td className="px-3 py-2 text-xs text-right">{u.ai_batch_count}</td>
                    <td className="px-3 py-2 text-xs text-right">{u.llm_requests}</td>
                    <td className="px-3 py-2 text-xs text-right">{formatCompact(u.cache_hit_tokens)}</td>
                    <td className="px-3 py-2 text-xs text-right">{formatCompact(u.cache_miss_tokens)}</td>
                    <td className="px-3 py-2 text-xs text-right font-medium text-gray-800">{formatCompact((u.cache_hit_tokens || 0) + (u.cache_miss_tokens || 0))}</td>
                    <td className="px-3 py-2 text-xs text-right">{formatCompact(u.output_tokens)}</td>
                    <td className="px-3 py-2 text-xs text-right font-medium text-green-700">{fmtCost(u.estimated_cost)}</td>
                    <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{u.last_active ? timeAgo(u.last_active) : '—'}</td>
                    <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                      <select
                        value={u.plan || 'basic'}
                        onChange={e => updateUserPlan(u.user_id, e.target.value)}
                        className="text-xs border rounded px-2 py-1"
                      >
                        <option value="basic">Basic</option>
                        <option value="plus">Plus</option>
                        <option value="extra">Extra</option>
                      </select>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => resetPassword(u.user_id, u.email)}
                        className="text-xs text-blue-600 hover:text-blue-800 hover:underline mr-3"
                        title="Generate a temporary password to send to this user"
                      >
                        Reset PW
                      </button>
                      <button
                        onClick={() => deleteUser(u.user_id, u.email)}
                        className="text-xs text-red-600 hover:text-red-800 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                  {selectedUserId === u.user_id && (
                    <tr>
                      <td colSpan={13} className="bg-gray-50 px-4 py-4 border-t">
                        {userDetailLoading && <div className="text-sm text-gray-400">Loading details…</div>}
                        {!userDetailLoading && userDetail && (
                          <UserDetailPanel detail={userDetail} range={range} corpora={corpora} onSaveCorpora={saveUserCorpora} />
                        )}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        </div>
        )
      })()}

      {activeTab === 'plans' && (
        <div className="space-y-4">
          {plans.map(plan => (
            <div key={plan.plan_name} className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="font-semibold capitalize mb-4">{plan.plan_name}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Daily Limit (AI units)</label>
                  <input
                    type="number"
                    defaultValue={plan.daily_limit}
                    onBlur={e => updatePlan(plan.plan_name, Number(e.target.value), plan.monthly_limit)}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Monthly Limit (AI units)</label>
                  <input
                    type="number"
                    defaultValue={plan.monthly_limit}
                    onBlur={e => updatePlan(plan.plan_name, plan.daily_limit, Number(e.target.value))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'config' && config && (
        <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
          <div className="border-b pb-4 mb-4">
            <h3 className="font-semibold text-gray-800 mb-1">API Keys</h3>
            <p className="text-xs text-gray-500">Keys are encrypted before storage. Existing values are not displayed for security.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">DeepSeek API Key</label>
            <input
              type="password"
              placeholder="sk-..."
              onBlur={e => {
                if (e.target.value.trim()) updateConfig('deepseek_api_key', e.target.value.trim())
              }}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gemini API Key</label>
            <input
              type="password"
              placeholder="AIza..."
              onBlur={e => {
                if (e.target.value.trim()) updateConfig('gemini_api_key', e.target.value.trim())
              }}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>

          <div className="border-t pt-4 mt-4">
            <h3 className="font-semibold text-gray-800 mb-3">Model Settings</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">DeepSeek Model</label>
            <input
              type="text"
              defaultValue={config.deepseek_model}
              onBlur={e => updateConfig('deepseek_model', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gemini Image Model</label>
            <input
              type="text"
              defaultValue={config.gemini_image_model}
              onBlur={e => updateConfig('gemini_image_model', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Temperature</label>
            <input
              type="number"
              step={0.1}
              defaultValue={config.temperature}
              onBlur={e => updateConfig('temperature', Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Tokens</label>
            <input
              type="number"
              defaultValue={config.max_tokens}
              onBlur={e => updateConfig('max_tokens', Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">AI Batch Size</label>
            <input
              type="number"
              defaultValue={config.ai_batch_size}
              onBlur={e => updateConfig('ai_batch_size', Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">Rows per LLM call (50–1000).</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">AI Max Rows</label>
            <input
              type="number"
              min={50}
              max={5000}
              defaultValue={config.ai_max_rows}
              onBlur={e => updateConfig('ai_max_rows', Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">Max rows a single AI analysis can process (50–5000).</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Image Generation Cost (AI units)</label>
            <input
              type="number"
              min={1}
              max={10000}
              defaultValue={config.image_generation_cost}
              onBlur={e => updateConfig('image_generation_cost', Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">Cost deducted from user's daily/monthly AI unit budget for each diagram generated.</p>
          </div>

          <div className="border-t pt-4 mt-4">
            <h3 className="font-semibold text-gray-800 mb-1">Token Weights</h3>
            <p className="text-xs text-gray-500 mb-3">AI units charged against plan budgets. Effective units = (cache_hit×w + cache_miss×w + output×w) / 1000.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {([
                ['cache_hit_weight', 'Cache Hit Weight'],
                ['cache_miss_weight', 'Cache Miss Weight'],
                ['output_weight', 'Output Weight'],
              ] as const).map(([field, label]) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input
                    type="number"
                    step={0.01}
                    min={0}
                    defaultValue={config[field]}
                    onBlur={e => { const n = Number(e.target.value); if (!Number.isNaN(n) && n >= 0) updateConfig(field, n) }}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <h3 className="font-semibold text-gray-800 mb-1">Pricing (per million tokens)</h3>
            <p className="text-xs text-gray-500 mb-3">Used for the dollar cost estimates in Overview and Users. Leave blank to disable cost tracking.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {([
                ['cache_hit_price', 'Cache Hit Price ($/M)'],
                ['cache_miss_price', 'Cache Miss Price ($/M)'],
                ['output_price', 'Output Price ($/M)'],
              ] as const).map(([field, label]) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input
                    type="number"
                    step={0.01}
                    min={0}
                    defaultValue={config[field] ?? ''}
                    onBlur={e => { const v = e.target.value.trim(); const n = parseFloat(v); updateConfig(field, v === '' || Number.isNaN(n) ? null : n) }}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'databases' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="font-semibold mb-3">Create a database</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input value={newDbName} onChange={e => setNewDbName(e.target.value)}
                  placeholder="e.g. Fibrosis" className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <input value={newDbDesc} onChange={e => setNewDbDesc(e.target.value)}
                  placeholder="Short description" className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
            </div>
            <button onClick={createDatabase} disabled={!newDbName.trim()}
              className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              Create database
            </button>
            <p className="text-xs text-gray-400 mt-2">A URL-safe slug is derived from the name; ingest manuscripts into it from the Ingestion tab.</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Name</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Slug</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Articles</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-600">Default for new users</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Description</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {corpora.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No databases yet. Create one above.</td></tr>
                )}
                {corpora.map(c => {
                  const editing = editDbId === c.corpus_id
                  return (
                  <tr key={c.corpus_id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium text-gray-800">
                      {editing ? (
                        <input
                          value={editDbName}
                          onChange={e => setEditDbName(e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          placeholder="Display name"
                          autoFocus
                        />
                      ) : c.name}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500" title="The slug (data folder) is fixed and not renamed">{c.slug}</td>
                    <td className="px-4 py-2 text-right">{c.article_count.toLocaleString()}</td>
                    <td className="px-4 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={c.is_default}
                        onChange={e => toggleDefaultDb(c, e.target.checked)}
                        disabled={editing}
                        title="New accounts are granted access to every database checked here"
                      />
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {editing ? (
                        <input
                          value={editDbDesc}
                          onChange={e => setEditDbDesc(e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          placeholder="Description (optional)"
                        />
                      ) : (c.description || <span className="text-gray-300">—</span>)}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {editing ? (
                        <>
                          <button onClick={() => saveEditDb(c)} disabled={!editDbName.trim()}
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-40">Save</button>
                          <button onClick={cancelEditDb}
                            className="text-xs text-gray-500 hover:text-gray-700 hover:underline ml-3">Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEditDb(c)}
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline">Edit</button>
                          <button onClick={() => deleteDatabase(c)}
                            className="text-xs text-red-600 hover:text-red-800 hover:underline ml-3">Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'ingestion' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="font-semibold mb-2">Search EuropePMC and grow the corpus</h3>
            <p className="text-xs text-gray-500 mb-4">
              Fetches OA articles from EuropePMC matching your query, skips PMCIDs already in the corpus,
              parses the full-text XML, and appends new rows to the metadata + partitioned text data.
              Use <em>Preview</em> first to sanity-check the result count.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Target database</label>
                <select
                  value={ingCorpusId}
                  onChange={e => setIngCorpusId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                >
                  <option value="">— Select a database —</option>
                  {corpora.map(c => (
                    <option key={c.corpus_id} value={c.corpus_id}>{c.name} ({c.article_count.toLocaleString()} articles)</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">New manuscripts are appended to this database. Create databases in the Databases tab.</p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Query</label>
                <input
                  type="text"
                  value={ingQuery}
                  onChange={e => setIngQuery(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder='e.g. "extracellular vesicles"'
                />
                <p className="text-xs text-gray-500 mt-1">
                  {ingQueryMode === 'structured'
                    ? 'Searched in Title, Abstract, and Keywords as a phrase.'
                    : 'Sent to EuropePMC exactly as written. You must include your own year and field filters.'}
                </p>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Query mode</label>
                <div className="flex gap-3">
                  <label className="inline-flex items-center gap-1.5 text-sm">
                    <input
                      type="radio"
                      name="queryMode"
                      value="structured"
                      checked={ingQueryMode === 'structured'}
                      onChange={() => setIngQueryMode('structured')}
                    />
                    Structured
                  </label>
                  <label className="inline-flex items-center gap-1.5 text-sm">
                    <input
                      type="radio"
                      name="queryMode"
                      value="raw"
                      checked={ingQueryMode === 'raw'}
                      onChange={() => setIngQueryMode('raw')}
                    />
                    Raw
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {ingQueryMode === 'structured'
                    ? 'Precise fielded search (ABSTRACT/TITLE/KEYWORD) with the year range built for you. Fewer false positives but may miss body-text mentions.'
                    : 'Passthrough mode: your query is sent exactly as written to EuropePMC. Only IN_EPMC:y (and OPEN_ACCESS:Y if checked) are appended automatically. Write the full query yourself — including year ranges and fielded syntax.'}
                </p>
                <div className="mt-2 p-2 bg-gray-50 rounded border text-xs font-mono text-gray-600 break-all">
                  EuropePMC will receive:{' '}
                  {ingQueryMode === 'structured'
                    ? `((ABSTRACT:"${ingQuery}") OR (TITLE:"${ingQuery}") OR (KEYWORD:"${ingQuery}")) AND (FIRST_PDATE:[${ingYearFrom}-01-01 TO ${ingYearTo}-12-31]) AND IN_EPMC:y${ingOaOnly ? ' AND OPEN_ACCESS:Y' : ''}`
                    : `${ingQuery}${ingQuery.toUpperCase().includes('IN_EPMC') || ingIncludeNonOa ? '' : ' AND IN_EPMC:y'}${ingOaOnly && !ingQuery.toUpperCase().includes('OPEN_ACCESS') ? ' AND OPEN_ACCESS:Y' : ''}`}
                </div>
              </div>

              {ingQueryMode === 'structured' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year from</label>
                    <input
                      type="number"
                      value={ingYearFrom}
                      onChange={e => setIngYearFrom(Number(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year to</label>
                    <input
                      type="number"
                      value={ingYearTo}
                      onChange={e => setIngYearTo(Number(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                </>
              )}
              <div className="col-span-2">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={ingIncludeNonOa}
                    onChange={e => setIngIncludeNonOa(e.target.checked)}
                  />
                  Include non-OA (abstract-only) — also ingest non-OA PMC articles and Medline-only records (PMID-based, abstract-only)
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Off (default): Open Access only — full text is fetched and indexed.
                  On: also pull non-OA PMC and Medline-only records, which have no full
                  text and are searchable by abstract/title only.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={previewIngestion}
                disabled={ingPreviewLoading || ingStartLoading || !ingQuery.trim()}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 disabled:opacity-50"
              >
                {ingPreviewLoading ? 'Searching…' : 'Preview'}
              </button>
              <button
                onClick={startIngestion}
                disabled={ingStartLoading || ingPreviewLoading || !ingQuery.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {ingStartLoading ? 'Starting…' : 'Start ingestion'}
              </button>
              {ingPreview && (
                <span className="text-sm text-gray-700">
                  Found <strong>{ingPreview.total_found.toLocaleString()}</strong> matches
                  · <strong>{ingPreview.total_new.toLocaleString()}</strong> new
                  {ingIncludeNonOa && ingPreview.total_oa !== undefined && (
                    <span className="text-gray-500">
                      {' '}({ingPreview.total_oa.toLocaleString()} OA · {(ingPreview.total_non_oa ?? 0).toLocaleString()} non-OA)
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="px-4 py-3 border-b bg-gray-50">
              <h3 className="font-semibold text-sm">Recent ingestion jobs</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Started</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Database</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Query</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Found</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">New</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Progress</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Status</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {ingJobs.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-6 text-center text-gray-400">
                      No ingestion jobs yet.
                    </td>
                  </tr>
                ) : (
                  ingJobs.map(j => {
                    const total = j.total_new || 0
                    const pct = total > 0 ? Math.min(100, Math.round((j.processed / total) * 100)) : 0
                    return (
                      <tr key={j.job_id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-600 text-xs" title={j.started_at || ''}>{timeAgo(j.started_at)}</td>
                        <td className="px-4 py-2 text-gray-700 text-xs">
                          {corpora.find(c => c.corpus_id === j.corpus_id)?.name || '—'}
                        </td>
                        <td className="px-4 py-2 max-w-xs truncate" title={j.params?.query || ''}>
                          {j.params?.query || '—'}
                          <span className="text-xs text-gray-400 ml-1">
                            ({j.params?.year_from}-{j.params?.year_to}{j.params?.oa_only ? ', OA' : ''}{j.params?.include_non_oa ? ', +non-OA' : ''})
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-700">{j.total_found.toLocaleString()}</td>
                        <td className="px-4 py-2 text-gray-700">{j.total_new.toLocaleString()}</td>
                        <td className="px-4 py-2 w-48">
                          {j.status === 'running' || j.status === 'complete' ? (
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded h-2 overflow-hidden">
                                <div className="bg-blue-500 h-full" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="text-xs text-gray-500 w-14 text-right">
                                {j.processed}/{total || '?'}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${INGEST_STATUS_STYLES[j.status]}`}>
                            {j.status}
                          </span>
                          {j.errors > 0 && (
                            <span className="ml-2 text-xs text-red-600" title="parse/fetch errors">
                              ⚠ {j.errors}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => reuseIngestionJob(j)}
                              disabled={!j.params}
                              className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 disabled:opacity-40"
                              title="Load this query into the form above to edit and re-launch"
                            >
                              Reuse
                            </button>
                            {(j.status === 'pending' || j.status === 'running') && (
                              <button
                                onClick={() => cancelIngestion(j.job_id)}
                                className="text-xs px-2 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100"
                              >
                                Cancel
                              </button>
                            )}
                            {j.reverted_at ? (
                              <span
                                className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded"
                                title={`Articles deleted ${timeAgo(j.reverted_at)}`}
                              >
                                Reverted
                              </span>
                            ) : (
                              (j.status === 'complete' || j.status === 'cancelled' || j.status === 'error') && (
                                <button
                                  onClick={() => revertIngestion(j)}
                                  className="text-xs px-2 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100"
                                  title="Remove the articles this job added to its database"
                                >
                                  Delete added
                                </button>
                              )
                            )}
                          </div>
                          {j.status === 'error' && j.error_message && (
                            <span className="text-xs text-red-600 block mt-1" title={j.error_message}>
                              {j.error_message.slice(0, 50)}…
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'backup' && (
        <div className="space-y-6">
          {/* User database */}
          <div className="bg-white rounded-xl shadow-sm border p-5">
            <h2 className="text-lg font-semibold text-gray-800 mb-1">User database</h2>
            <p className="text-sm text-gray-500 mb-4">
              Accounts, sessions, datasets, AI jobs, usage and config. Exported as a portable
              <code className="mx-1 px-1 bg-gray-100 rounded text-xs">.json.gz</code>. Restore merges by ID (nothing is deleted).
              <span className="block mt-1 text-amber-600">Note: the export includes encrypted API keys / app config — keep the file safe.</span>
            </p>
            {backupInfo?.user_db_tables && (
              <div className="flex flex-wrap gap-2 mb-4 text-xs">
                {Object.entries(backupInfo.user_db_tables).map(([t, n]: [string, any]) => (
                  <span key={t} className="px-2 py-1 bg-gray-100 rounded text-gray-700">
                    {t}: <span className="font-medium">{Number(n).toLocaleString()}</span>
                  </span>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-3 items-center">
              <button
                onClick={() => downloadBlob('/admin/backup/user-db', 'user-db-export')}
                disabled={!!backupBusy}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {backupBusy === 'user-db-export' ? 'Exporting…' : 'Export user database'}
              </button>
              <input
                ref={userDbFileRef}
                type="file"
                accept=".gz,.json"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) restoreUserDb(f) }}
              />
              <button
                onClick={() => userDbFileRef.current?.click()}
                disabled={!!backupBusy}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
              >
                {backupBusy === 'user-db-restore' ? 'Restoring…' : 'Restore from file…'}
              </button>
            </div>
          </div>

          {/* Manuscripts database */}
          <div className="bg-white rounded-xl shadow-sm border p-5">
            <h2 className="text-lg font-semibold text-gray-800 mb-1">Manuscripts databases</h2>
            <p className="text-sm text-gray-500 mb-4">
              The full-text Parquet corpora searched by the app. Export <strong>everything</strong> or a
              <strong> single database</strong> as a <code className="mx-1 px-1 bg-gray-100 rounded text-xs">.tar.gz</code> archive.
              {backupInfo && (
                <span className="ml-1">
                  Total size: <span className="font-medium text-gray-700">{formatBytes(backupInfo.manuscripts_bytes)}</span>
                </span>
              )}
              <span className="block mt-1 text-amber-600">
                Restoring installs each database in the archive into its own slot — databases <em>not</em> in the
                archive are left untouched. Runs in the background via the worker; replaced files are kept under
                <code className="mx-1 px-1 bg-gray-100 rounded text-xs">.restore_prev</code>.
              </span>
            </p>

            {/* Per-database sizes */}
            {backupInfo?.corpora?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4 text-xs">
                {backupInfo.corpora.map((c: any) => (
                  <span key={c.corpus_id} className="px-2 py-1 bg-gray-100 rounded text-gray-700">
                    {c.name}: <span className="font-medium">{formatBytes(c.bytes)}</span>
                    {!c.present && <span className="text-red-600"> (empty)</span>}
                  </span>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-3 items-center">
              <select
                value={exportCorpusId}
                onChange={e => setExportCorpusId(e.target.value)}
                disabled={!!backupBusy}
                className="px-3 py-2 border rounded-lg text-sm bg-white disabled:opacity-50"
                title="Choose what to export"
              >
                <option value="">Everything (all databases)</option>
                {corpora.map(c => (
                  <option key={c.corpus_id} value={c.corpus_id}>{c.name}</option>
                ))}
              </select>
              <button
                onClick={() => downloadBlob(
                  exportCorpusId ? `/admin/backup/manuscripts?corpus_id=${exportCorpusId}` : '/admin/backup/manuscripts',
                  'manuscripts-export',
                )}
                disabled={!!backupBusy || !backupInfo?.manuscripts_present}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {backupBusy === 'manuscripts-export' ? 'Preparing download…' : 'Export'}
              </button>
              <input
                ref={manuscriptFileRef}
                type="file"
                accept=".gz,.tgz,.tar"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) restoreManuscripts(f) }}
              />
              <button
                onClick={() => manuscriptFileRef.current?.click()}
                disabled={!!backupBusy}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
              >
                {backupBusy === 'manuscripts-restore' ? 'Restoring…' : 'Restore from file…'}
              </button>
              {backupBusy === 'manuscripts-restore' && manuscriptRestoreState && (
                <span className="text-sm text-blue-600">{manuscriptRestoreState}</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Per-user drill-down rendered inside the expanded Users-tab row: plan caps with
// daily/monthly usage bars (and a LIMIT REACHED badge), token/cost totals for the
// selected range, and a small daily-activity chart.
function UserDetailPanel({ detail, range, corpora, onSaveCorpora }: { detail: any; range: string; corpora: Corpus[]; onSaveCorpora: (userId: string, corpusIds: string[]) => void }) {
  const caps = detail.usage_caps || {}
  const totals = detail.totals || {}
  const daily: any[] = detail.daily || []
  const [accessIds, setAccessIds] = useState<Set<string>>(new Set(detail.corpus_ids || []))
  useEffect(() => { setAccessIds(new Set(detail.corpus_ids || [])) }, [detail.user_id, detail.corpus_ids])
  const toggleAccess = (id: string) => setAccessIds(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n
  })

  const UsageBar = ({ label, used, limit }: { label: string; used: number; limit: number }) => {
    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0
    const reached = limit > 0 && used >= limit
    return (
      <div>
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-gray-600">{label}</span>
          <span className="text-xs font-medium text-gray-800">
            {used} / {limit}
            {reached && (
              <span className="ml-2 bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-[10px] font-semibold">LIMIT REACHED</span>
            )}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full ${reached ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* AI usage caps */}
        <div className="bg-white rounded-lg border p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm">AI usage (current period)</h4>
            <span className="bg-gray-100 px-2 py-0.5 rounded text-xs">{detail.plan}</span>
          </div>
          <UsageBar label="Daily AI units" used={caps.used_daily ?? 0} limit={caps.daily_limit ?? 0} />
          <UsageBar label="Monthly AI units" used={caps.used_monthly ?? 0} limit={caps.monthly_limit ?? 0} />
          <p className="text-[11px] text-gray-400">
            Raising the plan lifts these caps immediately — usage is re-measured against the new ceiling on the next request.
          </p>
        </div>

        {/* Token + cost totals for the range */}
        <div className="bg-white rounded-lg border p-4">
          <h4 className="font-semibold text-sm mb-3">Totals ({range === 'all' ? 'all time' : range})</h4>
          <div className="grid grid-cols-2 gap-y-2 text-sm">
            <span className="text-gray-500">Searches</span><span className="text-right font-medium">{totals.search_count ?? 0}</span>
            <span className="text-gray-500">AI batches</span><span className="text-right font-medium">{totals.ai_batch_count ?? 0}</span>
            <span className="text-gray-500">LLM requests</span><span className="text-right font-medium">{totals.llm_requests ?? 0}</span>
            <span className="text-gray-500">Cache hit / miss</span><span className="text-right font-medium">{formatCompact(totals.cache_hit_tokens)} / {formatCompact(totals.cache_miss_tokens)}</span>
            <span className="text-gray-500">Output tokens</span><span className="text-right font-medium">{formatCompact(totals.output_tokens)}</span>
            <span className="text-gray-500">Est. cost</span><span className="text-right font-medium text-green-700">{fmtCost(totals.estimated_cost)}</span>
          </div>
          <div className="mt-3 pt-3 border-t text-[11px] text-gray-400">
            Joined {detail.created_at ? new Date(detail.created_at).toLocaleDateString() : '—'} · Last active {detail.last_active ? timeAgo(detail.last_active) : '—'}
          </div>
        </div>
      </div>

      {/* Daily activity */}
      {daily.length > 0 && (
        <div className="bg-white rounded-lg border p-4">
          <h4 className="font-semibold text-sm mb-3">Daily activity</h4>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={daily} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip content={<DailyTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="searches" stackId="a" fill="#3b82f6" name="Searches" />
              <Bar dataKey="ai_batch" stackId="a" fill="#8b5cf6" name="AI batch" />
              <Bar dataKey="llm_requests" stackId="a" fill="#10b981" name="LLM req" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Database access */}
      <div className="bg-white rounded-lg border p-4">
        <h4 className="font-semibold text-sm mb-1">Database access</h4>
        <p className="text-xs text-gray-500 mb-2">Which databases this user can search. (Admins always see all.)</p>
        {corpora.length === 0 ? (
          <div className="text-xs text-gray-400">No databases yet.</div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {corpora.map(c => (
                <label key={c.corpus_id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={accessIds.has(c.corpus_id)} onChange={() => toggleAccess(c.corpus_id)} />
                  <span>{c.name}</span>
                  {c.is_default && <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">default</span>}
                </label>
              ))}
            </div>
            <button
              onClick={() => onSaveCorpora(detail.user_id, Array.from(accessIds))}
              className="mt-3 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700"
            >
              Save access
            </button>
          </>
        )}
      </div>
    </div>
  )
}
